/**
 * The proof chip: one rotating line under the fold's CTA.
 *
 * WHAT IT SAYS NOW, and the two rulings that got it here. It opens on the one
 * true number this page owns, the demo address on the liveSince clock, and
 * then rotates through the INVENTORY of that deploy: the database, the file
 * storage, the keys, each in the product's own words. It never shows another
 * hostname.
 *
 * THE HISTORY, kept because the next person will be tempted to rebuild it.
 * This component once probed ten real student apps from the reader's own
 * browser and rotated their addresses as proof. Two rulings ended that: the
 * founder masked the hostnames for the students' privacy (22 Aug), and the
 * masking read as bleeped profanity while the parade of addresses read as
 * "we sell DNS" (backend feedback, 23 Aug). The probe apparatus, the ten
 * hosts, the check store and the lamp states were DELETED rather than left
 * dormant: the compiler here refuses unused code, and the git history of
 * sv-main/apps/cloud-web holds every line if a wall of live apps ever becomes
 * a page of its own. If it does, rebuild it there, not here: the fold's job
 * is the inventory, and one component should not carry two arguments.
 *
 * WHAT SURVIVES OF THE OLD DOCTRINE, because it was never about the probes:
 * no fabricated timestamps, ever. The clock is liveSince's and starts when
 * the address bar's sequence actually lands in this tab; the chip says
 * NOTHING until it does. The inventory sentences carry no clocks and no
 * counts we did not already render elsewhere: 6 tables is panel-rows' own
 * number. Under prefers-reduced-motion the rotation stops and the designed
 * still frame is the address, the lamp and the words.
 */

import { useEffect, useRef, useState } from 'react';

import { StatusPill } from './StatusPill';
import { elapsedLine, useLiveSince } from '../../screens/marketing/liveSince';
import { t } from '../../lib/i18n/t';
import '../../styles/components/live-feed.css';

/* -------------------------------------------------------------- the hooks */

function useStill(): boolean {
  const [still, setStill] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const resolve = () => setStill(media.matches);
    resolve();
    media.addEventListener('change', resolve);
    return () => media.removeEventListener('change', resolve);
  }, []);

  return still;
}

/* ----------------------------------------------------------- the rotation */

/** One dissolve, both halves. Matched to --t-state in live-feed.css. */
const FADE_MS = 250;

/**
 * A cadence between four and eight seconds, and never the same one twice in a
 * row. Rounding to a tenth of a second is what makes "never the same twice" a
 * comparison rather than a coin flip against forty one thousand values: there
 * are forty one cadences, a repeat is visible, so a repeat is rejected.
 */
function nextCadence(previous: number): number {
  let ms = previous;
  while (ms === previous) ms = 4000 + Math.round(Math.random() * 40) * 100;
  return ms;
}

interface Rotation {
  index: number;
  shown: boolean;
}

/**
 * The carousel, and why it is off unless a caller asks for it.
 *
 * The page keeps one thing moving at a time. On the fold that one thing is
 * chapter 2's address bar, so the chip picks one of the ten at random per page
 * load and holds still for the visit, and the plate shows all ten at once and
 * needs no rotation to be complete. `rotate` turns the carousel on for a caller
 * who wants it: a randomized four to eight second cadence, a dissolve rather
 * than a slide, and no bounce anywhere, because this page's motion vocabulary
 * is calm.
 *
 * THE BEAT IS A REAL CHECK. Roughly every third swap re-asks the host it is
 * moving to, so the lamp is neutral while the request is out and flips to Live
 * when the host answers. That is the product's own behaviour rendered at its
 * own speed. The rejected version was a scripted "building" beat, and it was
 * rejected for the same reason the counters are not seeded: we do not watch
 * these builds, so a build lamp on somebody else's app would be the one
 * invented fact on a strip whose entire job is that nothing in it is invented.
 *
 * Under prefers-reduced-motion nothing here runs, and the frame the reader gets
 * is the designed one rather than a paused one: one address, one lamp, one word.
 */
function useRotation(rotate: boolean, still: boolean, count: number): Rotation {
  const [index, setIndex] = useState(0);
  const [shown, setShown] = useState(true);
  const cursor = useRef(0);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    if (!rotate || still || typeof window === 'undefined') return;

    const pending = timers.current;
    const after = (ms: number, run: () => void) => {
      pending.push(window.setTimeout(run, ms));
    };

    let cadence = 0;

    const swap = () => {
      setShown(false);
      after(FADE_MS, () => {
        cursor.current = (cursor.current + 1) % count;
        setIndex(cursor.current);
        setShown(true);
        schedule();
      });
    };

    const schedule = () => {
      cadence = nextCadence(cadence);
      after(cadence, swap);
    };

    schedule();

    return () => {
      pending.forEach((id) => window.clearTimeout(id));
      pending.length = 0;
      setShown(true);
    };
  }, [rotate, still, count]);

  return { index, shown };
}

/* ------------------------------------------------------------- the pieces */

/**
 * The lamp is the real StatusPill, so the chip inherits the shape coding, the
 * word and the --state-live token from the one component that owns them. Only
 * the live state exists here now: the checking and quiet states belonged to
 * the probe apparatus, and the apparatus is gone (see the header).
 */
function StateCell() {
  return (
    <span className="lfeed__state">
      <StatusPill value="live" size="sm" />
    </span>
  );
}

function Address({ host }: { host: string }) {
  // Plain text on purpose, not a link: there is nothing to navigate to, the
  // address is the demo's own.
  return <span className="lfeed__addr mono">{host}</span>;
}

/* -------------------------------------------------------------- the chip */

/** The fold's own demo address, which chapter 2 builds on this tab's clock. */
const HERO_HOST = 'nina-recipes.apps.sv-academy.org';

/**
 * What the rotation says, and why none of it is a hostname any more.
 *
 * FOUNDER AND BACKEND RULING, 23 Aug 2026. The chip used to rotate the ten
 * student apps with their names masked, and the masking read as bleeped
 * profanity while the parade of addresses read as "we sell DNS". Both wrong,
 * and the second one wronger: the platform's argument is that the address
 * COMES WITH a database, file storage and keys, so the proof line now rotates
 * the inventory of the demo deploy instead of other people's addresses. The
 * three sentences are the demo's own facts, the same fiction the checklist
 * and the panels already render: 6 tables is panel-rows' number, and nothing
 * here claims a real world event.
 *
 * The hero entry keeps the ONE true number (the liveSince clock), and the
 * whole line still says nothing until that clock exists, because the first
 * thing the rotation shows must be the thing that is true.
 */
const INVENTORY = [
  'A database came with it. 6 tables, already connected.',
  'File storage came with it. The photos already load.',
  'Keys and settings came with it, set before first deploy.',
] as const;

function Chip({ rotate }: { rotate: boolean }) {
  const still = useStill();
  const { seconds } = useLiveSince();

  // Entry 0 is the address on its true clock; entries 1..3 are the inventory.
  const { index, shown } = useRotation(rotate, still, INVENTORY.length + 1);
  const live = seconds !== null;
  const addressTurn = index === 0 || !live;

  return (
    <div className="lfeed" data-variant="chip">
      {/* The frame is what dissolves, so the address, the lamp and the counter
          cross together and never disagree for a frame about what they are
          describing. The row reserves its height either way, so nothing under
          the fold moves when the clock starts. */}
      <div className="lfeed__frame" data-shown={shown ? 'yes' : 'no'}>
        {addressTurn ? (
          live ? (
            <>
              <Address host={HERO_HOST} />
              <StateCell />
              {/* A counter is the only moving part here, so a reader who asked
                  for no motion does not get one. The designed still frame is
                  the address and the word. */}
              {still ? null : <span className="lfeed__meter mono">{elapsedLine(seconds)}</span>}
            </>
          ) : null
        ) : (
          <span className="lfeed__meter mono">{t(INVENTORY[index - 1])}</span>
        )}
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- the door */

export interface LiveFeedProps {
  /**
   * Off by default, and the default is the design. See useRotation: the page
   * keeps one thing moving at a time. Turning this on gives the chip a four
   * to eight second carousel over the demo address and its inventory.
   */
  rotate?: boolean;
}

/**
 * THE PLATE VARIANT IS GONE, founder ruling 22 Aug 2026, late: the arrival
 * chapter it lived in was removed outright, ledger form and lamp form both.
 * The chip is the one mount left, so the chip is the component. The check
 * machinery above stayed whole on purpose: it serves the chip today and it is
 * the piece a future cohort surface would want back first.
 */
export function LiveFeed({ rotate = false }: LiveFeedProps) {
  return <Chip rotate={rotate} />;
}

export default LiveFeed;
